import { ErrorDetailsDtoErrorCodeEnum, Role } from '@interfaces/models';
import { useGetCostCenters } from '@shared/hooks/use-get-cost-centers';
import { AssignmentRoles, getRoleColor } from '@shared/utils';
import useDeepCompareEffect from 'use-deep-compare-effect';
import { useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Badge, toast } from '@ui/components';
import { useUsers } from '@pages/users/hooks';
import { useForm } from 'react-hook-form';
import { useCallback } from 'react';
import { TFunction } from 'i18next';
import { z } from 'zod';

const USER_FORM_DEFAULT_VALUES = {
  firstName: '',
  lastName: '',
  email: '',
  role: '',
  costCenters: [],
};

const formSchema = (t: TFunction) =>
  z.object({
    firstName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 }))
      .nonempty(t('FORMS.REQUIRED')),
    lastName: z
      .string({ required_error: t('FORMS.REQUIRED') })
      .trim()
      .min(3, t('FORMS.MIN_CHARACTERS', { limit: 3 }))
      .max(50, t('FORMS.MAX_CHARACTERS', { limit: 50 }))
      .nonempty(t('FORMS.REQUIRED')),
    email: z.string({ required_error: t('FORMS.REQUIRED') }).email(t('FORMS.INVALID_EMAIL')),
    role: z.string({ required_error: t('FORMS.REQUIRED') }).nonempty(t('FORMS.REQUIRED')),
    costCenters: z.array(z.string()).min(1, t('FORMS.REQUIRED')),
  });

export const useUserInvite = (onCreateUserSuccess: () => void = () => {}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const {
    costCentersOptions,
    defaultCostCenter,
    isFetching: isCostCentersFetching,
  } = useGetCostCenters();

  const form = useForm({
    resolver: zodResolver(formSchema(t)),
    defaultValues: USER_FORM_DEFAULT_VALUES,
  });

  const { createUser, isCreateUserPending } = useUsers({
    onCreateUserSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      form.reset();
      toast.success(t('USERS.INVITE_USER_SIDEBAR_SUCCESS'));
      onCreateUserSuccess();
    },
    onCreateUserError: ({ errorCode }) => {
      if (errorCode === ErrorDetailsDtoErrorCodeEnum.USER_INVITE_EMAIL_NOT_UNIQUE) {
        return form.setError('email', {
          message: t(`API_ERRORS.${errorCode}`),
          type: 'custom',
        });
      }

      toast.error(t(`API_ERRORS.DEFAULT`));
    },
  });

  const roleOptions =
    AssignmentRoles.map((role) => ({
      label: (
        <Badge variant={getRoleColor(role)} size="large">
          {t(`AUTH.ROLE.${role}`)}
        </Badge>
      ),
      value: role,
    })) || [];

  const onSubmit = useCallback(() => {
    const value = form.getValues();
    createUser({ ...value, role: value.role as Role });
  }, [form, createUser]);

  const selectedCostCenters = form.watch('costCenters');

  useDeepCompareEffect(() => {
    if (!selectedCostCenters.length && defaultCostCenter) {
      form.setValue('costCenters', [defaultCostCenter.id]);
    }
  }, [defaultCostCenter, selectedCostCenters]);

  return {
    isCostCentersFetching,
    isCreateUserPending,
    costCentersOptions,
    roleOptions,
    onSubmit,
    form,
    t,
  };
};
